/**
 * SOBA — the room two members are in when they are on the same island at the same time.
 *
 * Pure, like the rest of `core`, and for the usual reason: the browser and the Worker have to agree
 * exactly on what a message is, and one implementation cannot drift from itself. The client encodes
 * with these functions and the room validates with the same ones.
 *
 * ## What this is for
 *
 * The map has always drawn other members — `core/posada.ts` places everybody active in the last
 * thirty days beside the task that is their next step. That layer is a photograph: it is derived
 * from progress, it is the same for every viewer, and nobody in it is really there.
 *
 * This is the other layer. Somebody who has the map open right now walks, and you see the steps.
 * They are drawn with the same sprite and the same gait as your own character, and the only thing
 * that distinguishes them from the photograph is that they move. Deliberately the only thing: a
 * community of a few hundred people is mostly asleep, and a feature that announces „nobody is here"
 * advertises loneliness rather than curing it. When the room is empty the map is the map it has
 * always been, and nothing anywhere says so.
 *
 * ## What it is not
 *
 * There is no text. The four gestures below are the entire vocabulary, they are fixed, and they are
 * broadcast to the island rather than aimed at a person — which is why there is nothing to moderate
 * and no way to single anybody out. Words live on Skool, exactly as `core/posada.ts` already argues
 * for comments. There is no count of who is present, no badge, no notification: the moment the app
 * says „people are on the map right now", SADA stops being the first screen (GDD §2①).
 *
 * ## Extending it
 *
 * Two rules make every future addition — a duel, a collaboration, a guild hall, an island that
 * remembers what was built on it — additive rather than breaking:
 *
 *   1. **An unrecognised kind is ignored, in both directions.** `decode` returns null and the
 *      caller drops it. A phone still running last month's bundle does not crash on a message from
 *      a feature it has never heard of; it simply does not see it.
 *   2. **The room name carries its own kind.** `ostrvo:…` is an island. `esnaf:…` would be a guild
 *      hall and `dvoboj:…` an arena, all served by the same object with the same presence code,
 *      because presence is the part none of them want to write twice.
 *
 * The one primitive those features share and this one does not need is an invitation — offer,
 * accept, session — because a duel and a collaboration are both „two people agreed to something".
 * It is deliberately not built here. A round trip nobody consumes is an unfinished feature, not
 * groundwork; the groundwork is that adding `{ t: 'poziv', to, kind }` later breaks nothing.
 */

/**
 * Bump when the meaning of any message changes.
 *
 * A deploy replaces the whole Worker at once but every open tab keeps the bundle it loaded, so an
 * old client will talk to a new room. The room answers a mismatch by closing the socket, and the
 * client falls back to the photograph — which is exactly how it behaves when the network is gone.
 * Degrade, never break.
 */
export const SOBA_VERSION = 1;

/**
 * The four things one member can say to another.
 *
 * Four, and the pressure to add a fifth will be constant. The test each one has to pass is whether
 * it gets worse when it is sent forty times: a question or an offer does not, praise does. That is
 * also why none of them is evaluative — a quality judgement between members is the thing GDD §10.1
 * says poisons a community that lives on feedback.
 *
 * All four are gender-free. Half the crew are the female bodies that shipped with the world
 * builder, and a masculine past participle would be wrong for them.
 *
 * `ne-ide` and `pogledacu` are a pair: the ask and the answer, the feedback loop compressed into
 * two button presses. They are the reason gestures exist at all; the other two are the social floor
 * that keeps the pair from reading as a transaction.
 */
export const GESTOVI = ['cao', 'hvala', 'ne-ide', 'pogledacu'] as const;
export type Gest = (typeof GESTOVI)[number];

/** Which way somebody is facing, as an index. The renderer's own names, in a fixed order. */
export const FACINGS = ['up', 'down', 'left', 'right'] as const;
export type Facing = (typeof FACINGS)[number];

/* ------------------------------------------------------------------ limits */

/**
 * How far an offset from an island's centre may legitimately be.
 *
 * The largest island the builder will draw is 64×64 (`MAX_ISLAND_SIZE`), so half of that plus the
 * water somebody can row into around it. Anything past this is not a member walking, and the room
 * drops it rather than storing a coordinate that would put a body in the middle of the ocean.
 */
export const MAX_OFFSET = 96;

/** Longest raw frame the room will even parse. A position update is about sixty bytes. */
export const MAX_FRAME_BYTES = 512;

/**
 * The fastest anybody can legitimately move.
 *
 * The engine spends 165 ms on a tile, so a member walking flat out produces about six steps a
 * second. Twice that leaves room for a phone that stalled and then delivered four updates at once,
 * and still refuses a position that jumped across the island.
 *
 * Refusing rather than punishing is the posture GDD §8.4 already takes with everything else that
 * cannot be verified: there is nothing to win by teleporting on a map with no contest on it, so the
 * check exists to keep a bug from looking like a person, not to catch a cheat.
 */
export const MAX_TILES_PER_SECOND = 12;

/** Least time between two accepted position updates from one member. The client sends at 120 ms. */
export const POSITION_MIN_MS = 90;

/** Least time between two accepted gestures. Slow enough that a bubble cannot be made to strobe. */
export const GESTURE_MIN_MS = 2_500;

/**
 * Frames one member may send in ten seconds before the room stops listening.
 *
 * Generous: six steps a second is sixty position updates, and the keepalive never reaches the
 * handler at all. A client hitting this is broken or hostile, and either way the answer is the
 * same — close the socket and let the reconnect settle it.
 */
export const FRAME_BUDGET = 120;
export const FRAME_WINDOW_MS = 10_000;

/**
 * How often the client says it is still there. Comfortably inside the edge's idle timeout.
 *
 * There is deliberately **no client-side timer that forgets a quiet member.** An earlier version had
 * one, and it was wrong in a way that only shows up with three people: somebody who stands still
 * reading a task sends nothing at all — the engine reports a position only when one changes — so a
 * timer keyed on "when did we last hear about them" deletes the people who are doing exactly what
 * the curriculum asks. Departures are the room's job, and it is good at it: a socket that closes
 * fires `webSocketClose`, and one that dies silently stops answering the keepalive, so the edge
 * closes it within a couple of minutes and the same handler runs.
 *
 * The residual case — a body left standing because nobody noticed — is indistinguishable from the
 * standing crew, which is what the map has always looked like. It degrades into the product's
 * normal state rather than into people disappearing while you watch.
 */
export const KEEPALIVE_MS = 30_000;

/**
 * The keepalive, and the exact string the room auto-answers.
 *
 * Registered with `setWebSocketAutoResponse`, which means the runtime replies without waking the
 * object: a room where four people are standing still costs nothing at all, which is the entire
 * argument for the hibernation API. It has to be an exact match on both sides, so it is spelled
 * once, here, rather than built by `encode` at each end.
 */
export const KEEPALIVE_FRAME = '{"t":"k"}';
export const KEEPALIVE_REPLY = '{"t":"k"}';

/* ------------------------------------------------------------------ the room name */

/**
 * Which room an island is, at a given publish.
 *
 * The content version is in the name because a publish can change what an island is: add a task and
 * the trail through it moves. Two members holding different versions would be describing different
 * ground, so they are simply put in different rooms until the older one refreshes — which the client
 * already does on its own. Nobody sees a stranger standing in a tree; at worst they briefly do not
 * see somebody who is there.
 *
 * The path is deliberately NOT in the name. It used to have to be: an island's shape was grown from
 * the viewer's own task list, so TEMELJ was nineteen tiles wide on ADOBE and seventeen on DAVINCI
 * and the two could not share coordinates. `carveIsland` now grows every island from its full task
 * list and hangs only the viewer's own signposts on it, so the ground is identical and the two paths
 * walk it together — which is what GDD §5.1 says they are doing anyway, in different software.
 */
export function islandRoom(contentVersion: number, islandId: string): string {
  return `ostrvo:${contentVersion}:${islandId}`;
}

/**
 * The arena. The second room kind, and the reason the name carries its kind at all.
 *
 * No content version in it, unlike an island: a duel has no ground. Its two people agreed on a
 * brief and a clock, both of which are rows in D1 rather than tiles, so a publish in the middle of
 * a duel changes nothing they are looking at and must not split them into two rooms.
 *
 * The duel id is already unguessable, and the Worker checks membership before it forwards, so the
 * name is not carrying any secret either way.
 */
export function duelRoom(duelId: string): string {
  return `dvoboj:${duelId}`;
}

/**
 * Somebody's own island. The third kind.
 *
 * **The revision is in the name, and it is doing the same job the content version does for an
 * island.** An owner rearranging their island while a guest is standing on it is an ordinary thing
 * to want, and the moment they save, the guest is looking at ground that no longer exists — a tree
 * that is not there, a beach that is now sea. Rather than forbid the rearranging or invent a lock,
 * the two are simply put in different rooms until the guest picks up the new revision, which the
 * client does the moment the room tells it the number moved. Briefly not seeing somebody is the
 * accepted failure everywhere else in this file; watching each other walk through solid ground is
 * not.
 *
 * **The member id in the name is not a secret and must never be treated as one.** Unlike a duel id,
 * it is published: it rides on every `Present`, it keys the peer card, and `POST /api/dvoboj` takes
 * it as a request field. Knowing whose island this is has to be worth nothing — the Worker checks
 * the door before it forwards, and that check is the only thing standing between a closed island and
 * a stranger.
 */
export function domRoom(memberId: string, rev: number): string {
  return `dom:${memberId}:${rev}`;
}


/* ------------------------------------------------------------------ where somebody is */

/**
 * A position, as an offset in tiles from the island's authored centre.
 *
 * This is the one thing the wire may never get wrong, so it is worth being explicit about why it is
 * not simply a map coordinate. `buildWorld` lays the archipelago out starting from the islands *the
 * viewer can see*, so its origin moves as somebody unlocks islands — two members looking at the same
 * sea have different numbers for it. `island.map` does not move: it is authored content, the same
 * for everybody, and both the generated ellipse and a drawn canvas are centred on it. Measuring from
 * there is the only description of a position that means the same thing on both screens.
 */
export interface Offset {
  x: number;
  y: number;
}

/** The island's centre, in the coordinates of one particular viewer's world. */
export interface Anchor {
  x: number;
  y: number;
}

export const toOffset = (anchor: Anchor, tile: { x: number; y: number }): Offset => ({
  x: tile.x - anchor.x,
  y: tile.y - anchor.y,
});

export const fromOffset = (anchor: Anchor, offset: Offset): { x: number; y: number } => ({
  x: anchor.x + offset.x,
  y: anchor.y + offset.y,
});

/* ------------------------------------------------------------------ people */

/**
 * Somebody in the room, as everybody else is allowed to see them.
 *
 * The same shape `core/posada.ts` already permits — a name, a look, earned souvenirs, the task they
 * are on — and no address, ever. It rides along with the room rather than being looked up, because
 * a member you have just met may legitimately be missing from the crew view (that list is filtered
 * by shared islands and a thirty-day window), and walking up to somebody has to open a card with a
 * person in it.
 */
export interface Present {
  id: string;
  name: string;
  look: number;
  souvenirs: string[];
  /** The task they are working on, for the card. Null when nothing on their map is open. */
  nodeId: string | null;
  x: number;
  y: number;
  facing: Facing;
  /** True while a step is in flight, so a body that stops mid-stride settles rather than freezing. */
  moving: boolean;
}

/* ------------------------------------------------------------------ the messages */

/**
 * Position uses one-letter keys and nothing else does.
 *
 * It is the only message on a hot path — six a second per moving member, fanned out to everybody in
 * the room — and it is the only one where the envelope is a meaningful share of the payload. Every
 * other message is rare enough that being readable in a network log is worth more than the bytes.
 */
export type ToRoom =
  | { t: 'p'; x: number; y: number; f: number; m: 0 | 1 }
  | { t: 'g'; g: number }
  /** Keepalive. Answered by the runtime without waking the room — see `setWebSocketAutoResponse`. */
  | { t: 'k' };

export type FromRoom =
  | { t: 'soba'; v: number; you: string; people: Present[] }
  | { t: 'dolazak'; person: Present }
  | { t: 'odlazak'; id: string }
  | { t: 'p'; id: string; x: number; y: number; f: number; m: 0 | 1 }
  | { t: 'g'; id: string; g: number }
  /**
   * Say where you are again.
   *
   * A room with nobody talking is evicted from memory, and positions are not worth persisting — so
   * when it wakes it knows who is connected and not where they are standing. Rather than store a
   * coordinate on every step, it asks. Everyone answers once and the room is whole again.
   */
  | { t: 'gde' }
  /**
   * Something happened to a duel. Added by the feature in `docs/DVOBOJ.md`, and the first use of
   * rule 1 at the top of this file: a client that predates it drops this on the floor and carries
   * on, so `SOBA_VERSION` does not move.
   *
   * It is a nudge, never the truth — D1 is. `poziv` is relayed into the island room the challenger
   * is standing in, so somebody beside them sees it land; everything after acceptance goes to the
   * arena. The payload is only what the battle screen needs to animate the moment without waiting
   * for a fetch; it reconciles against the server straight afterwards.
   */
  | { t: 'dvoboj'; duel: string; ev: DuelEvent; side?: 0 | 1; axis?: string }
  /**
   * Somebody in this room changed how they look.
   *
   * The second use of rule 1 at the top of this file, and the reason that rule was worth writing
   * down: a tab from before the wardrobe existed drops this and carries on drawing that person the
   * way they were when it last heard about them, so `SOBA_VERSION` does not move and nobody
   * standing on an island is disconnected by the deploy that ships this.
   *
   * It carries the *whole* appearance as the one integer `core/look.ts` keeps it as, which is why
   * there is nothing else in the payload — and it is server-originated. The socket may only ever
   * move its own body (see the note at the top of `server/soba.ts`); an appearance is vouched for
   * by the Worker, which read it back out of D1, exactly like the name and the souvenirs already
   * on the seat.
   */
  | { t: 'izgled'; id: string; look: number }
  | { t: 'kraj'; reason: KrajReason };

/**
 * What happened to it. Serbian kinds, like the gestures and the hang-up reasons.
 *
 * `odgovor` covers accept, decline and counter-offer as one event on purpose: all three mean „the
 * terms changed, go and look", and splitting them would put the duel's state machine into the
 * protocol as well as into `core/dvoboj.ts`, where it already is.
 */
export const DUEL_EVENTS = ['poziv', 'odgovor', 'predaja', 'glas', 'ishod', 'prekid'] as const;
export type DuelEvent = (typeof DUEL_EVENTS)[number];

/**
 * Why the room hung up.
 *
 * None of these is ever shown to anybody — there is no copy for them and there should not be. They
 * exist so the client can tell a door that is shut from a network that dropped: `zatvoreno` is the
 * only one worth another attempt, and everything else means stop asking. Getting that wrong is a
 * tab quietly reconnecting to a locked island every few seconds for as long as it is open.
 */
export type KrajReason =
  /** This client is from before the room's protocol. It must reload to come back. */
  | 'verzija'
  /** Out of the crew, or membership lapsed. */
  | 'van-posade'
  /** Not their island — or an island that stopped existing in a publish they have not seen. */
  | 'zakljucano'
  /**
   * Another tab of the same member took the room.
   *
   * Its own reason and not `zatvoreno`, because the two want opposite handling and getting it wrong
   * is a loop: the room allows one body per member, so if the tab that was replaced reconnects it
   * evicts the tab that replaced it, which reconnects, which evicts it back — twice a second,
   * forever, with everybody flickering off both maps and the room woken continuously.
   */
  | 'zamenjen'
  /** Ordinary: the room let go. Reconnecting is correct. */
  | 'zatvoreno'
  /** Talking far faster than walking allows. Broken or hostile; the backoff decides the rest. */
  | 'previse';

/* ------------------------------------------------------------------ codec */

const isFiniteInt = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value);

const inRange = (value: number, limit: number): boolean => value >= -limit && value <= limit;

/**
 * Read a frame, or refuse it.
 *
 * Null covers three different things on purpose — malformed, oversized, and simply not recognised —
 * because every caller does the same thing with all three: nothing. An unknown `t` is how a client
 * from before a feature existed survives a room that has it, so "I do not know this" must be as
 * ordinary and as silent as "this is not JSON".
 */
export function decodeToRoom(raw: string | ArrayBuffer): ToRoom | null {
  if (typeof raw !== 'string' || raw.length > MAX_FRAME_BYTES) return null;
  let body: unknown;
  try {
    body = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!body || typeof body !== 'object') return null;
  const message = body as Record<string, unknown>;

  switch (message.t) {
    case 'p': {
      const { x, y, f, m } = message;
      if (!isFiniteInt(x) || !isFiniteInt(y) || !isFiniteInt(f)) return null;
      if (!inRange(x, MAX_OFFSET) || !inRange(y, MAX_OFFSET)) return null;
      if (f < 0 || f >= FACINGS.length) return null;
      return { t: 'p', x, y, f, m: m === 1 ? 1 : 0 };
    }
    case 'g': {
      const { g } = message;
      if (!isFiniteInt(g) || g < 0 || g >= GESTOVI.length) return null;
      return { t: 'g', g };
    }
    case 'k':
      return { t: 'k' };
    default:
      return null;
  }
}

/**
 * Read a frame from the room.
 *
 * Shallow validation rather than deep: the room is our own Worker and cannot be impersonated, so
 * this is guarding against a version skew — a message shaped differently by a deploy — not against
 * an attacker. What matters is that an unrecognised or malformed frame is dropped instead of
 * throwing inside an event handler where nothing would catch it.
 */
export function decodeFromRoom(raw: string | ArrayBuffer): FromRoom | null {
  if (typeof raw !== 'string' || raw.length > 64 * 1024) return null;
  let body: unknown;
  try {
    body = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!body || typeof body !== 'object') return null;
  const message = body as Record<string, unknown>;

  switch (message.t) {
    case 'soba':
      if (!isFiniteInt(message.v) || typeof message.you !== 'string') return null;
      if (!Array.isArray(message.people)) return null;
      return {
        t: 'soba',
        v: message.v,
        you: message.you,
        people: message.people.filter(isPresent),
      };
    case 'dolazak':
      return isPresent(message.person) ? { t: 'dolazak', person: message.person } : null;
    case 'odlazak':
      return typeof message.id === 'string' ? { t: 'odlazak', id: message.id } : null;
    case 'p': {
      const { id, x, y, f, m } = message;
      if (typeof id !== 'string') return null;
      if (!isFiniteInt(x) || !isFiniteInt(y) || !isFiniteInt(f)) return null;
      if (f < 0 || f >= FACINGS.length) return null;
      return { t: 'p', id, x, y, f, m: m === 1 ? 1 : 0 };
    }
    case 'g': {
      const { id, g } = message;
      if (typeof id !== 'string') return null;
      if (!isFiniteInt(g) || g < 0 || g >= GESTOVI.length) return null;
      return { t: 'g', id, g };
    }
    case 'gde':
      return { t: 'gde' };
    case 'dvoboj': {
      const { duel, ev, side, axis } = message;
      if (typeof duel !== 'string' || duel.length === 0 || duel.length > 64) return null;
      if (!(DUEL_EVENTS as readonly unknown[]).includes(ev)) return null;
      const out: FromRoom = { t: 'dvoboj', duel, ev: ev as DuelEvent };
      if (side === 0 || side === 1) out.side = side;
      if (typeof axis === 'string' && axis.length <= 16) out.axis = axis;
      return out;
    }
    case 'izgled': {
      const { id, look } = message;
      if (typeof id !== 'string' || !isFiniteInt(look) || look < 0) return null;
      return { t: 'izgled', id, look };
    }
    case 'kraj':
      return { t: 'kraj', reason: (message.reason as KrajReason) ?? 'zatvoreno' };
    default:
      return null;
  }
}

function isPresent(value: unknown): value is Present {
  if (!value || typeof value !== 'object') return false;
  const person = value as Record<string, unknown>;
  return (
    typeof person.id === 'string' &&
    typeof person.name === 'string' &&
    typeof person.look === 'number' &&
    Array.isArray(person.souvenirs) &&
    (person.nodeId === null || typeof person.nodeId === 'string') &&
    isFiniteInt(person.x) &&
    isFiniteInt(person.y) &&
    typeof person.facing === 'string' &&
    (FACINGS as readonly string[]).includes(person.facing) &&
    typeof person.moving === 'boolean'
  );
}

export const encode = (message: ToRoom | FromRoom): string => JSON.stringify(message);

export const facingOf = (index: number): Facing => FACINGS[index] ?? 'down';
export const facingIndex = (facing: Facing): number => Math.max(0, FACINGS.indexOf(facing));
export const gestOf = (index: number): Gest | null => GESTOVI[index] ?? null;

/* ------------------------------------------------------------------ the speed check */

/**
 * Is this step physically possible, given when the last one was?
 *
 * Shared so the room and the renderer cannot disagree about it. The room drops what it refuses; the
 * client uses the same answer to decide whether to walk a body to its new tile or simply put it
 * there — a jump that could not have been walked is a dropped packet, and animating one would drag
 * somebody sideways through a cliff.
 */
export function plausibleStep(
  from: { x: number; y: number },
  to: { x: number; y: number },
  elapsedMs: number,
): boolean {
  const distance = Math.abs(to.x - from.x) + Math.abs(to.y - from.y);
  if (distance === 0) return true;
  const allowed = (Math.max(elapsedMs, 0) / 1000) * MAX_TILES_PER_SECOND + 1;
  return distance <= allowed;
}

/** True when a body should be walked to its new tile rather than simply placed there. */
export const walkable = (from: { x: number; y: number }, to: { x: number; y: number }): boolean =>
  Math.abs(to.x - from.x) + Math.abs(to.y - from.y) === 1;
